import { SimpleActivity } from "@ac-expo/api-types";
import { BuyableItemWithPricing, useTicketing } from "@ac-expo/hooks";
import { Button, cn, DrawerFooter } from "@ac-expo/ui-components";
import {
  createPartialCompleteBuyableItem,
  getBuyableItemGroupKey,
  isTicketingAvailable,
  isTicketTypeWithinSalePeriod,
} from "@ac-expo/utils";
import { useUserContext } from "@web-app/src/context/auth/user.context";
import { useCartContext } from "@web-app/src/context/cart.context";
import { format, isAfter } from "date-fns";
import { FC, useMemo } from "react";
import { formatPrice } from "../Animation/DetailsModal";
import { SingleItemTicketing } from "../Ticket/SingleItemTicketing";

type ActivityDetailsCartPartProps = {
  activity: SimpleActivity;
  close: () => void;
};

export const ActivityDetailsCartPart: FC<ActivityDetailsCartPartProps> = ({ activity, close }) => {
  const { toggleCart } = useCartContext();
  const { isAuthenticated } = useUserContext();
  const ticketTypes = (activity?.ticketTypes ?? []).filter(isTicketTypeWithinSalePeriod);
  const activityTicketType = ticketTypes.at(0);

  const now = format(new Date(), "yyyy-MM-dd");
  const isRegisterEnabled =
    activity &&
    (isTicketingAvailable(activity.event) ||
      activity.schedules.some((s) => isAfter(s.timetable.day, now)));

  const availableEntries = useMemo((): BuyableItemWithPricing[] => {
    if (!activityTicketType) return [];
    return [
      {
        buyableItem: createPartialCompleteBuyableItem({
          buyableType: "ticket",
          ticketType: {
            id: activityTicketType.id,
            name: activityTicketType.name,
            quantityLeft: activityTicketType.quantityLeft,
            groupMinSize: activityTicketType.groupMinSize,
            event: { id: activity.eventId, name: activity.event?.name ?? activity.name },
          },
        }),
        pricing: activityTicketType.pricing,
      },
    ];
  }, [activityTicketType, activity.eventId, activity.event?.name, activity.name]);

  const { localCart, handleAddToCart, isPending } = useTicketing({
    availableEntries,
    activityTicketTypes: ticketTypes,
    basketType: "store",
    isAuthenticated,
    onAddToCartMutationSuccess: () => {
      close();
      toggleCart(true);
    },
  });

  const { localPricing } = localCart;
  const total = localPricing.total;
  const discount = localPricing.discount;

  if (!isRegisterEnabled || activityTicketType === undefined) {
    return null;
  }

  const itemKey = getBuyableItemGroupKey({
    buyableType: "ticket",
    ticketType: { id: activityTicketType.id },
  });

  return (
    <>
      <SingleItemTicketing
        entry={{ entryType: activityTicketType, itemKey }}
        localCart={localCart}
      />
      <DrawerFooter className="flex w-full flex-row items-center !justify-between border-t border-t-gray-300 pt-6">
        <div className="flex flex-col gap-1">
          <p className="text-2xl font-bold">
            <span
              className={cn(discount > 0 && "text-sm text-gray-500 line-through")}
            >{`${formatPrice(total)}`}</span>
            {discount > 0 && <span>{` ${formatPrice(total - discount)}`}</span>}
          </p>
          <p className="text-sm font-medium text-gray-500">TOTAL</p>
        </div>
        <Button isLoading={isPending} variant="blue" onClick={handleAddToCart}>
          Ajouter au panier
        </Button>
      </DrawerFooter>
    </>
  );
};
